import React from 'react';
import {useTranslation} from "react-i18next";
import {formatNumberFloat} from "../../../../utils/helpers";
import StatusBadge from "../../../../components/StatusBadge";
import {PaperAirplaneIcon, EyeIcon, FlagIcon} from "@heroicons/react/24/outline";
import SortIcon from "../../../../components/SortIcon";
import {PAYD_URL} from "../../../../utils/env";
import {Link} from "react-router-dom";
import ButtonLink from "../../../../components/ButtonLink";
import Button from "../../../../components/Button";
import ButtonInternalLink from "../../../../components/ButtonInternalLink";
import {cn} from "../../../../utils/style";
import useAuthStore from "../../../../store/auth";

function CompactPaymentList({handleChangeSort, records, sort, setLinkToShow}) {
    const {t} = useTranslation();
    const {merchant} = useAuthStore();

    if (!records?.data?.length) return (
        <div className="bg-white rounded-[2rem] p-10 text-center shadow-sm border border-gray-100">
            <p className="text-gray-400 font-medium">No payments found</p>
        </div>
    );

    return (
        <div className="space-y-4">
            <div className="hidden lg:block">
                <div className="grid grid-cols-12 gap-4 px-8 py-3 text-xs font-bold uppercase tracking-widest text-gray-400">
                    <div className="col-span-3">Invoice Details</div>
                    <div className="col-span-3">Customer Details</div>
                    <div className="col-span-2">Status & Environment</div>
                    <div className="col-span-2">Reference Info</div>
                    <div className="col-span-2 text-right">Actions</div>
                </div>
            </div>

            <div className="space-y-3">
                {records?.data?.map((record) => (
                    <div key={record?.paymentUuid} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200 group">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                            {/* Invoice Details */}
                            <div className="col-span-1 lg:col-span-3 space-y-1">
                                <div className="flex items-center gap-2 mb-2 lg:hidden">
                                    <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Invoice Details</span>
                                </div>
                                <Row title="ID / Ref" value={record?.paymentUuid} valueClassName="font-mono text-gray-900 font-bold text-sm"/>
                                <Row title="Amount" 
                                     value={`${formatNumberFloat(record?.amount, ['DA', 'DZD'].includes(record?.currency?.symbol) ? 'fr-DZ' : 'en-US')} ${record?.currency?.symbol || ''}`}
                                     valueClassName="text-[#0b6a8c] font-black text-sm"
                                />
                                <Row title="Created By" value={record?.user?.email}/>
                                <Row title="Date" value={record?.createdAt}/>
                                {merchant?.allowSubVendors === true && <Row title="Supplier" value={record?.supplier?.supplierName}/>}
                            </div>

                            {/* Customer Details */}
                            <div className="col-span-1 lg:col-span-3 space-y-1">
                                <div className="flex items-center gap-2 mb-2 lg:hidden">
                                    <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Customer</span>
                                </div>
                                <Row title="Name" value={record?.name} valueClassName="truncate font-bold text-gray-900 text-sm"/>
                                <Row title="Email" value={record?.email}/>
                                <Row title="Mobile" value={record?.phone}/>
                                <Row title="Remarks" value={record?.remarks} valueClassName="line-clamp-1 italic text-gray-400"/>
                            </div>

                            {/* Status & Environment */}
                            <div className="col-span-1 lg:col-span-2 space-y-1">
                                <div className="flex items-center gap-2 mb-2 lg:hidden">
                                    <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Status & Environment</span>
                                </div>
                                <Row title="Status" value={<StatusBadge status={record?.status}/>}/>
                                <Row title="Env" value={record?.test ?
                                    <span className="text-xs font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-100 uppercase">Test</span> :
                                    <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-100 uppercase">Live</span>
                                }/>
                                <Row title="Paid At" value={record?.paidAt} valueClassName="whitespace-nowrap overflow-visible"/>
                                {record?.antiFraud && (
                                    <Row title="Fraud" value={<FlagIcon className="h-3 w-3 text-rose-500"/>}/>
                                )}
                            </div>

                            {/* Reference Info */}
                            <div className="col-span-1 lg:col-span-2 space-y-1">
                                <div className="flex items-center gap-2 mb-2 lg:hidden">
                                    <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Reference</span>
                                </div>
                                <Row title="Cust Ref" value={record?.customerReferenceNumber}/>
                                <Row title="Payment ID" value={record?.gatewayReferenceNumber}/>
                                <Row title="Auth Code" value={record?.authCode}/>
                                <Row title="Card No" value={record?.cardNumber} valueClassName="font-mono"/>
                            </div>

                            {/* Actions */}
                            <div className="col-span-1 lg:col-span-2 flex lg:flex-col items-center lg:items-end justify-end gap-2">
                                <button
                                    className="w-full lg:w-24 text-xs font-bold uppercase tracking-wider bg-white text-gray-600 border border-gray-200 py-2 rounded-full hover:bg-gray-50 hover:border-cyan-300 hover:text-[#0b6a8c] transition-all shadow-sm"
                                    onClick={() => {
                                        setLinkToShow(`${PAYD_URL}/pay/${record?.paymentUuid}`)
                                    }}>
                                    Share
                                </button>
                                <ButtonInternalLink
                                    className="w-full lg:w-24 justify-center !px-0 !py-2 !text-xs tracking-wider uppercase"
                                    href={`/merchant/payments/view/${record?.paymentUuid}`}>
                                    View
                                </ButtonInternalLink>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


function Row({title, value, valueClassName, className}) {
    return (
        <div className={cn("flex items-center gap-2 text-xs", className)}>
            <span className="font-bold text-gray-500 uppercase tracking-tight w-20 shrink-0 text-nowrap">{title}:</span>
            {value ? (
                <span className={cn("text-gray-600 font-medium truncate", valueClassName)}>{value}</span>
            ) : (
                <span className="text-gray-300 font-medium">N/A</span>
            )}
        </div>
    );
}

export default CompactPaymentList;
